import React, { useMemo } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Platform } from 'react-native';

const isWeb = Platform.OS === 'web';

const MEDIA_TYPE_ICONS = {
  image: 'image',
  video: 'video',
  music: 'music',
  '3d': 'cube',
};

// Keys that are shown elsewhere in the lightbox or should never appear as chips
const SKIP_KEYS = new Set(['action', 'style', 'negative_prompt', 'prompt', 'description', 'text', 'lyrics']);

// displayNames that are too obvious/cluttering to show in chips
const SKIP_DISPLAY_NAMES = new Set(['Number of Images', 'Style']);

// displayNames that should be renamed
const RENAME_DISPLAY_NAMES = { 'Duration in seconds': 'Duration' };

function buildParamDefMap(model) {
  if (!model?.parameters || !Array.isArray(model.parameters)) return {};
  const map = {};
  for (const p of model.parameters) {
    if (p?.name) map[p.name] = p;
  }
  return map;
}

function resolveParamLabel(def, value) {
  if (!def) return String(value);
  const options = def.validation?.options || def.customParamSchema?.validation?.options;
  if (options && Array.isArray(options)) {
    const match = options.find(o => o.value === value || o.id === value);
    if (match) return match.label || match.name || String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function getParamIcon(def) {
  return def?.ui?.displayIcon || def?.customParamSchema?.ui?.displayIcon || null;
}

function getParamName(def, key) {
  return def?.ui?.displayName || def?.customParamSchema?.ui?.displayName || key;
}

const ExploreDetailParameters = React.memo(({ parameters, modelSlug, mediaType, musicContext, sourceImage, categories, onCategoryPress }) => {
  const { colors } = useTheme();
  const model = useModelStore(state => state.getModelBySlug(modelSlug));

  const paramChips = useMemo(() => {
    if (!parameters || typeof parameters !== 'object') return [];
    const defMap = buildParamDefMap(model);
    const chips = [];

    for (const [key, value] of Object.entries(parameters)) {
      if (value == null || value === '') continue;

      const paramName = key.includes(':') ? key.split(':').pop() : key;
      if (SKIP_KEYS.has(paramName)) continue;

      // Skip objects and long strings — they don't fit in chips
      if (typeof value === 'object') continue;
      if (typeof value === 'string' && value.length > 60) continue;

      const def = defMap[paramName];
      const label = resolveParamLabel(def, value);
      const icon = getParamIcon(def);
      const rawDisplayName = getParamName(def, paramName);

      if (SKIP_DISPLAY_NAMES.has(rawDisplayName)) continue;

      const displayName = RENAME_DISPLAY_NAMES[rawDisplayName] ?? rawDisplayName;

      chips.push({ key, displayName, label, icon, hasDef: !!def });
    }

    // Deduplicate by normalized displayName — prefer the chip that has a model def
    const seen = new Map();
    for (const chip of chips) {
      const norm = chip.displayName.toLowerCase();
      const existing = seen.get(norm);
      if (!existing || (!existing.hasDef && chip.hasDef)) {
        seen.set(norm, chip);
      }
    }
    return Array.from(seen.values());
  }, [parameters, model]);

  const categoryChips = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories.filter(c => c && typeof c === 'object');
  }, [categories]);

  const hasContent = paramChips.length > 0 || categoryChips.length > 0;
  if (!hasContent) return null;

  return (
    <View style={styles.container}>
      {mediaType && (
        <View style={[styles.chip, { backgroundColor: colors.solid_color_tertiary }]}>
          <Icon name={MEDIA_TYPE_ICONS[mediaType] || 'circle'} size={12} color={colors.text_color_secondary} />
          <Text size="xs" weight="medium" color="secondary">
            {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
          </Text>
        </View>
      )}

      {paramChips.map(chip => (
        <View key={chip.key} style={[styles.chip, { backgroundColor: colors.solid_color_tertiary }]}>
          {chip.icon && <Icon name={chip.icon} size={11} color={colors.text_color_secondary} />}
          <Text size="xs" color="secondary">{chip.displayName}:</Text>
          <Text size="xs" weight="medium" color="primary">{chip.label}</Text>
        </View>
      ))}

      {categoryChips.map(cat => (
        <Pressable
          key={cat.id || cat._id}
          onPress={() => onCategoryPress?.(cat.slug)}
          style={[styles.chip, styles.categoryChip, { backgroundColor: colors.solid_color_tertiary }, isWeb && { cursor: 'pointer' }]}
        >
          {cat.icon && <Icon name={cat.icon} size={10} color={colors.text_color_tertiary} />}
          <Text size="xs" weight="medium" color="secondary">{cat.name}</Text>
        </Pressable>
      ))}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  categoryChip: {
    gap: 4,
  },
});

ExploreDetailParameters.displayName = 'ExploreDetailParameters';

export default ExploreDetailParameters;
